import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import SectionLabel from "../components/SectionLabel";
import SEO from "../components/SEO";
import StructuredData, {
    ACT_LOCAL_BUSINESS_SCHEMA,
} from "../components/StructuredData";
import InquiryForm from "../components/forms/InquiryForm";
import CallbackForm from "../components/forms/CallbackForm";
import { BRAND } from "../data/content";

const TABS = [
    { id: "inquiry", label: "Detailed Inquiry" },
    { id: "callback", label: "Request a Callback" },
];

export default function Contact() {
    const [tab, setTab] = useState("inquiry");
    const underlineRefs = useRef({});
    const [underlineRect, setUnderlineRect] = useState(null);

    // Animate the gold underline between tabs.
    useEffect(() => {
        const el = underlineRefs.current[tab];
        if (el) {
            const parent = el.offsetParent;
            if (parent) {
                setUnderlineRect({
                    left: el.offsetLeft,
                    width: el.offsetWidth,
                });
            }
        }
    }, [tab]);

    return (
        <div data-testid="contact-page">
            <SEO
                title="Book a Consultation — Luxury Smart Home Integration"
                description="Private consultation with the ACT concierge. Call 803.728.9097 or email concierge@actluxe.net. New engagements accepted by appointment only."
                canonical="https://actluxe.net/contact"
            />
            <StructuredData data={ACT_LOCAL_BUSINESS_SCHEMA} />

            {/* Hero */}
            <section className="relative bg-[#0B0B0C] pt-20 md:pt-28 pb-12 md:pb-16 border-b border-[rgba(201,168,106,0.12)] overflow-hidden">
                <div className="shell">
                    <SectionLabel number="Contact" label="Concierge" />
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-20 mt-8 items-end">
                        <h1 className="lg:col-span-8 font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.05] max-w-4xl">
                            {"Begin the "}
                            <span className="italic text-[#C9A86A]">
                                Conversation.
                            </span>
                        </h1>
                        <p className="lg:col-span-4 text-[#9A958A] text-base md:text-lg leading-[1.85] max-w-md lg:pb-2">
                            We accept a small number of new engagements each
                            season. Every conversation begins privately,
                            without obligation, often under NDA.
                        </p>
                    </div>
                </div>
            </section>

            {/* Form + Direct Lines */}
            <section className="bg-[#0B0B0C] section-pad overflow-hidden">
                <div className="shell grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    {/* Form column */}
                    <div className="lg:col-span-7">
                        <SectionLabel
                            number="01"
                            label="Request a Private Consultation"
                        />

                        {/* Tabs */}
                        <div
                            role="tablist"
                            aria-label="Contact form mode"
                            className="relative mt-10 flex items-center gap-8 md:gap-10 border-b border-[rgba(201,168,106,0.15)]"
                            data-testid="contact-tabs"
                        >
                            {TABS.map((t) => {
                                const isActive = tab === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={isActive}
                                        aria-controls={`panel-${t.id}`}
                                        id={`tab-${t.id}`}
                                        ref={(el) => {
                                            underlineRefs.current[t.id] = el;
                                        }}
                                        onClick={() => setTab(t.id)}
                                        className={`relative pb-4 text-[11px] md:text-[12px] tracking-[0.28em] uppercase transition-colors duration-300 ${
                                            isActive
                                                ? "text-[#C9A86A]"
                                                : "text-[#9A958A] hover:text-[#F3EFE7]"
                                        }`}
                                        data-testid={`contact-tab-${t.id}`}
                                    >
                                        {t.label}
                                    </button>
                                );
                            })}
                            {/* Gold underline */}
                            {underlineRect && (
                                <motion.span
                                    layout
                                    aria-hidden="true"
                                    className="absolute -bottom-[1px] h-px bg-[#C9A86A]"
                                    initial={false}
                                    animate={{
                                        left: underlineRect.left,
                                        width: underlineRect.width,
                                    }}
                                    transition={{
                                        duration: 0.45,
                                        ease: [0.22, 1, 0.36, 1],
                                    }}
                                />
                            )}
                        </div>

                        <div className="mt-8 border border-[rgba(201,168,106,0.2)] p-8 md:p-12 bg-[#15161A]/40 relative">
                            <AnimatePresence mode="wait">
                                {tab === "inquiry" ? (
                                    <motion.div
                                        key="inquiry"
                                        role="tabpanel"
                                        id="panel-inquiry"
                                        aria-labelledby="tab-inquiry"
                                        suppressHydrationWarning
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{
                                            duration: 0.45,
                                            ease: [0.22, 1, 0.36, 1],
                                        }}
                                    >
                                        <InquiryForm />
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        key="callback"
                                        role="tabpanel"
                                        id="panel-callback"
                                        aria-labelledby="tab-callback"
                                        suppressHydrationWarning
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{
                                            duration: 0.45,
                                            ease: [0.22, 1, 0.36, 1],
                                        }}
                                    >
                                        <CallbackForm />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    {/* Direct Lines */}
                    <aside className="lg:col-span-5">
                        <SectionLabel number="02" label="Direct Lines" />
                        <div
                            className="mt-10 border border-[rgba(201,168,106,0.2)] bg-[#15161A]/40 p-8 md:p-10"
                            data-testid="contact-direct-lines"
                        >
                            <div className="space-y-8">
                                <div>
                                    <span className="eyebrow">Telephone</span>
                                    <a
                                        href={BRAND.phoneHref}
                                        className="mt-3 block font-serif text-3xl md:text-4xl text-[#F3EFE7] hover:text-[#C9A86A] transition-colors duration-400"
                                        data-testid="contact-phone-link"
                                    >
                                        {BRAND.phone}
                                    </a>
                                </div>

                                <div>
                                    <span className="eyebrow">Concierge</span>
                                    <a
                                        href={BRAND.emailHref}
                                        className="mt-3 block text-[#F3EFE7] text-base md:text-lg hover:text-[#C9A86A] transition-colors duration-400 break-all"
                                        data-testid="contact-email-link"
                                    >
                                        {BRAND.email}
                                    </a>
                                </div>

                                <div>
                                    <span className="eyebrow">Hours</span>
                                    <p className="mt-3 text-[#F3EFE7] text-base leading-[1.7]">
                                        By appointment · Monday – Saturday
                                    </p>
                                </div>

                                <div>
                                    <span className="eyebrow">Service Area</span>
                                    <p className="mt-3 text-[#F3EFE7] text-base leading-[1.7]">
                                        Private residences worldwide · Based in
                                        the United States
                                    </p>
                                </div>

                                <div className="hairline-solid" />

                                <div>
                                    <span className="eyebrow">Response Time</span>
                                    <p className="mt-3 text-[#9A958A] leading-[1.85] italic font-serif text-lg">
                                        We respond to every inquiry within one
                                        business day.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </section>

            {/* NDA closing band */}
            <section className="bg-[#0B0B0C] border-t border-[rgba(201,168,106,0.15)] overflow-hidden">
                <div className="shell py-10 md:py-12 text-center">
                    <p
                        className="text-[#9A958A] text-sm md:text-[15px] tracking-wide max-w-2xl mx-auto leading-[1.8]"
                        data-testid="contact-nda-band"
                    >
                        Prefer discretion? All inquiries are handled in strict
                        confidence,{" "}
                        <span className="text-[#C9A86A]">
                            under NDA on request.
                        </span>
                    </p>
                </div>
            </section>
        </div>
    );
}
